import type { StudentPackage } from '@aurea/contracts';
import { Button, Chip, clsx, Tray } from '@aurea/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, BookOpen, Pencil, Plus, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdjustDialog, PaymentDialog, SellDialog } from '../../components/BillingDialogs';
import { EnrollDialog, StudentCourses } from '../../components/GroupDialogs';
import { UserDialog } from '../../components/UserDialog';
import { api } from '../../lib/api';
import { fmtDate, fmtMoney, fmtShortDate, fmtTime } from '../../lib/time';
import { canManage, isAdmin, useMe } from '../_app';

export const Route = createFileRoute('/_app/people/$id')({ component: StudentPage });

function StudentPage() {
  const { id } = Route.useParams();
  const me = useMe();
  const admin = isAdmin(me);
  const manage = canManage(me);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const card = useQuery({ queryKey: ['student', id], queryFn: () => api.student(id) });
  const [dialog, setDialog] = useState<'sell' | 'pay' | 'enroll' | 'profile' | { adjust: string } | null>(
    null,
  );
  const s = card.data;

  if (card.isPending) return <div className="skeleton h-64" />;
  if (!s) return null;
  const owes = s.balance < 0;

  return (
    <>
      {admin && (
        <Link to="/people" className="btn-link mb-3 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="i size-4" aria-hidden="true" />
          {t('billing.back')}
        </Link>
      )}
      <header className="page-head">
        <div>
          <h1>
            {s.firstName} {s.lastName}
          </h1>
          <p>{[s.email, s.phone].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="flex-1" />
        {manage && (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setDialog('profile')} aria-label={t('users.edit')}>
              <Pencil className="i" />
            </Button>
            <Button variant="ghost" onClick={() => setDialog('pay')}>
              <Wallet className="i" />
              {t('billing.payment')}
            </Button>
            <Button onClick={() => setDialog('sell')}>
              <Plus className="i" />
              {t('billing.sell')}
            </Button>
          </div>
        )}
      </header>

      <div className="today-layout">
        <section aria-labelledby="pk-h">
          <h2 id="pk-h" className="mb-4 text-xl font-semibold">
            {t('billing.title')}
          </h2>
          {s.packages.length === 0 ? (
            <p className="tray tray-pad text-sm text-ink-2">{t('billing.noPackages')}</p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
              {s.packages.map((p) => (
                <PackageSticker
                  key={p.id}
                  p={p}
                  onAdjust={
                    manage && p.status !== 'cancelled' ? () => setDialog({ adjust: p.id }) : undefined
                  }
                />
              ))}
            </div>
          )}

          <h2 className="mt-8 mb-4 text-xl font-semibold">{t('billing.history')}</h2>
          <section className="tray overflow-x-auto">
            {s.history.length === 0 ? (
              <p className="p-4 text-sm text-ink-3">{t('billing.noHistory')}</p>
            ) : (
              <table className="table">
                <tbody>
                  {s.history.map((h) => (
                    <tr key={h.lessonId}>
                      <td className="whitespace-nowrap text-ink-2">
                        {fmtDate(h.startsAt, me.timezone, lang)}, {fmtTime(h.startsAt, me.timezone, lang)}
                      </td>
                      <td>
                        <span className="inline-flex items-center gap-1.5">
                          <Chip color={h.color} small />
                          {h.title}
                        </span>
                      </td>
                      <td className={h.attendance === 'absent' ? 'text-danger' : ''}>
                        {t(`billing.attendance.${h.attendance}`)}
                      </td>
                      <td
                        className={clsx(
                          'text-ink-3',
                          !h.charged &&
                            (h.attendance === 'present' || h.attendance === 'absent') &&
                            'font-medium !text-danger',
                        )}
                      >
                        {h.charged
                          ? t('billing.charged', { name: h.packageName ?? t('billing.title') })
                          : h.attendance === 'present'
                            ? t('billing.notCharged')
                            : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </section>

        <aside className="today-side flex flex-col gap-4">
          <Tray
            title={t('groups.courses')}
            icon={<BookOpen className="i" />}
            count={s.courses.length}
            action={
              manage && (
                <button type="button" className="btn-link text-sm" onClick={() => setDialog('enroll')}>
                  {t('groups.enroll')}
                </button>
              )
            }
          >
            <StudentCourses student={s} admin={manage} />
          </Tray>
          <Tray title={t('billing.balance')} icon={<Wallet className="i" />}>
            <p className={clsx('text-2xl font-bold tracking-tight', owes && 'text-danger')}>
              {fmtMoney(s.balance, lang)}
            </p>
            <p className="mt-1 text-sm text-ink-3">
              {s.debtLessons > 0
                ? t('billing.debtLessons', { lessons: t('plural.lessons', { count: s.debtLessons }) })
                : !owes && t('billing.balanceOk')}
            </p>
          </Tray>
          <Tray title={t('billing.payments')} count={s.payments.length}>
            {s.payments.length === 0 ? (
              <p className="text-sm text-ink-3">{t('billing.noPayments')}</p>
            ) : (
              <ul className="rows">
                {s.payments.map((p) => (
                  <li key={p.id}>
                    <div className="min-w-0 flex-1">
                      <b className="block font-semibold">{fmtMoney(p.amount, lang)}</b>
                      <span className="text-xs text-ink-3">
                        {[
                          fmtDate(p.paidAt, me.timezone, lang),
                          t(`billing.methods.${p.method}`),
                          p.packageName,
                          p.comment,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Tray>
        </aside>
      </div>

      {dialog === 'sell' && <SellDialog student={s} onClose={() => setDialog(null)} />}
      {dialog === 'pay' && <PaymentDialog student={s} onClose={() => setDialog(null)} />}
      {dialog === 'enroll' && <EnrollDialog student={s} onClose={() => setDialog(null)} />}
      {dialog === 'profile' && <UserDialog user={s} self={s.id === me.id} onClose={() => setDialog(null)} />}
      {dialog && typeof dialog === 'object' && (
        <AdjustDialog studentId={s.id} packageId={dialog.adjust} onClose={() => setDialog(null)} />
      )}
    </>
  );
}

/** Абонемент как стикер: остаток крупно, полоса израсходованного, срок и оплата. */
function PackageSticker({ p, onAdjust }: { p: StudentPackage; onAdjust?: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const left = p.lessonsTotal - p.lessonsUsed;
  const active = p.status === 'active';
  return (
    <article
      className={clsx('paper sticker flex flex-col gap-1 p-4', !active && 'opacity-60')}
      data-c={p.color ?? 'sand'}
    >
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold tracking-tight">{left}</span>
        <span className="text-sm text-[var(--sticker-ink-2)]">
          {t('billing.ofTotal', { total: p.lessonsTotal })}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/60" aria-hidden="true">
        <i
          className="block h-full rounded-full bg-[var(--sticker-ink)]"
          style={{ width: `${(p.lessonsUsed / p.lessonsTotal) * 100}%` }}
        />
      </div>
      <h3 className="mt-2 font-semibold leading-tight">{p.name}</h3>
      <p className="meta">
        {t(`billing.status.${p.status}`)} ·{' '}
        {p.validUntil ? t('billing.until', { date: fmtShortDate(p.validUntil, lang) }) : t('billing.forever')}
      </p>
      <p className={clsx('meta', p.paid < p.price && 'font-semibold !text-[#7A1F16]')}>
        {t('billing.paidOf', { paid: fmtMoney(p.paid, lang), price: fmtMoney(p.price, lang) })}
      </p>
      {onAdjust && (
        <div className="mt-auto pt-2">
          <button type="button" className="btn btn-paper-light" onClick={onAdjust}>
            {t('billing.adjust')}
          </button>
        </div>
      )}
    </article>
  );
}
